import { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { integrationsApi, type IntegrationProvider } from "@/lib/api";
import { Icon } from "@/components/ui/icon";

/** Set before leaving for Slack/Linear, so the callback knows which one sent the user back. */
export const PENDING_INTEGRATION_KEY = "ms_connecting_integration";

const NAMES: Record<IntegrationProvider, string> = { slack: "Slack", linear: "Linear" };

/** /integrations/callback?code=…&state=… — finishes a Slack or Linear connection. */
export function IntegrationCallbackPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const handled = useRef(false);
  const [provider] = useState<IntegrationProvider | null>(() => {
    const p = sessionStorage.getItem(PENDING_INTEGRATION_KEY);
    return p === "slack" || p === "linear" ? p : null;
  });

  useEffect(() => {
    if (handled.current) return;
    handled.current = true;
    sessionStorage.removeItem(PENDING_INTEGRATION_KEY);

    const back = () => navigate("/settings?tab=integrations", { replace: true });
    const code = searchParams.get("code");
    const state = searchParams.get("state");
    const error = searchParams.get("error");

    if (!provider || error || !code || !state) {
      toast.error(error === "access_denied" ? "Connection cancelled" : "Couldn't finish connecting. Try again.");
      back();
      return;
    }

    void integrationsApi.complete(provider, code, state).then((res) => {
      if ("data" in res) toast.success(`${NAMES[provider]} connected`);
      else toast.error(res.error.message);
      back();
    });
  }, []);

  return (
    <div className="flex h-full items-center justify-center gap-2 font-mono text-[11px] text-muted-foreground">
      <Icon name="refresh" size={12} className="animate-spin" />
      Connecting {provider ? NAMES[provider] : ""}…
    </div>
  );
}
